import { get, post, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Retrieves the current valid ID Token. 
 * fetchAuthSession automatically refreshes the session if expired.
 */
export const getIdToken = async () => {
    try {
        const session = await fetchAuthSession();
        // Use optional chaining as tokens might be undefined if not signed in
        return session.tokens?.idToken?.toString();
    } catch (err) {
        console.error("Error fetching auth session:", err);
        return null;
    }
};

export const callApi = async ({
    path,
    method = 'GET',
    queryParams = {},
    body = null,
    contentType = ''
}) => {
    // 1. Get the token (auto-refreshed by Amplify if necessary)
    const idToken = await getIdToken();

    if (!idToken) {
        throw new Error("User is not authenticated or session could not be refreshed.");
    }

    const headers = {
        Authorization: idToken,
    };

    if (contentType) {
        headers['Content-Type'] = contentType;
    }

    const options = {
        headers,
        queryParams,
        ...(body && method !== 'GET' && { body })
    };

    const restOp = {
        apiName: 'tsService',
        path,
        options,
    };

    const methodsMap = { GET: get, POST: post, PUT: put };
    const opFunc = methodsMap[method];
    if (!opFunc) throw new Error(`Unsupported method: ${method}`);

    try {
        const op = opFunc(restOp);
        const apiResponse = await op.response;
        return await apiResponse.body.json();
    } catch (error) {
        // If the token was valid but the backend still rejected it, 
        // you can force a refresh as a fallback:
        if (error.message?.includes('token has expired')) {
            const forcedSession = await fetchAuthSession({ forceRefresh: true });
            const newToken = forcedSession.tokens?.idToken?.toString();

            // Retry logic would go here if desired
            console.warn("Token was expired; forced a refresh.");
        }
        console.error("API call failed:", error);
        throw error;
    }
};

export const fetchPresignUrl = async (user, key, op, asHtml = false) => {
    return await callApi({
        path: `/ts-file-service/${op}`,
        queryParams: { key, asHtml },
    });
};
